import { useEffect, useMemo, useState } from 'react';
import { useAppStore } from '../store/appStore';
import { estimatePrint, formatDuration } from '../export/estimate';
import { runExport } from '../export/runExport';
import { usePreviewState } from './PreviewControls';
import { capturePlateThumbnail } from '../scene/PlateViewer';

export function ExportSheet() {
  const objects = useAppStore((s) => s.objects);
  const settings = useAppStore((s) => s.settings);
  const projectName = useAppStore((s) => s.projectName);
  const preview = usePreviewState();
  const [busy, setBusy] = useState(false);
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const heuristic = useMemo(() => estimatePrint(objects, settings), [objects, settings]);
  const est = preview.estimate && preview.status === 'ready' ? preview.estimate : heuristic;

  useEffect(() => {
    let alive = true;
    let url: string | null = null;
    void capturePlateThumbnail().then((buf) => {
      if (!alive || !buf?.length) return;
      const copy = new Uint8Array(buf.byteLength);
      copy.set(buf);
      url = URL.createObjectURL(new Blob([copy], { type: 'image/png' }));
      setThumbUrl(url);
    });
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [objects, projectName, preview.status, preview.layers.length]);

  const walls = Number(settings.wall_loops ?? 2);
  const lh = Number(settings.layer_height ?? 0.2);
  const infill = Number(settings.sparse_infill_density ?? 15);
  const pattern = String(settings.sparse_infill_pattern ?? 'grid');
  const support = Boolean(settings.enable_support);
  const adhesion = String(settings.brim_type ?? 'auto_brim') !== 'no_brim';

  async function onExport() {
    setBusy(true);
    try {
      await runExport();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="export-sheet" data-testid="export-sheet">
      <div className="export-card">
        <h2>Export project</h2>
        {thumbUrl && (
          <img className="export-thumb" data-testid="export-thumb" src={thumbUrl} alt="Plate thumbnail" />
        )}
        <p className="export-lede">
          {projectName || 'Untitled'} · {objects.length} object{objects.length === 1 ? '' : 's'}
        </p>
        <ul className="export-objects">
          {objects.map((o) => (
            <li key={o.id}>{o.name}</li>
          ))}
          {!objects.length && <li className="muted">No models on the plate</li>}
        </ul>
        <dl className="export-meta">
          <div><dt>Layer height</dt><dd>{lh.toFixed(2)} mm</dd></div>
          <div><dt>Walls</dt><dd>{walls}</dd></div>
          <div><dt>Infill</dt><dd>{infill}% {pattern}</dd></div>
          <div><dt>Supports</dt><dd>{support ? 'On' : 'Off'}</dd></div>
          <div><dt>Adhesion</dt><dd>{adhesion ? 'Brim' : 'Off'}</dd></div>
          <div><dt>Estimate</dt><dd>{objects.length ? `${formatDuration(est.approxMinutes)} · ${est.filamentGrams}g` : '—'}</dd></div>
        </dl>
        <p className="export-note">
          These settings are written into the project 3MF and honoured by Bambu cloud slicing.
        </p>
        <button
          type="button"
          className="primary export-cta"
          data-testid="export-btn"
          disabled={busy || !objects.length}
          onClick={() => void onExport()}
        >
          {busy ? 'Exporting…' : 'Export .3mf'}
        </button>
        <p className="export-flow">
          Upload the 3MF privately to MakerWorld, then send it to print from Bambu Handy.
        </p>
        {preview.status === 'ready' && preview.estimate && (
          <p className="export-note subtle">Path-based preview estimate · {preview.estimate.note}</p>
        )}
      </div>
    </div>
  );
}

